import React, { useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import {
  Button,
  ButtonGroup,
  Row,
  Col,
  Table,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from "reactstrap";
import Header from "../../../layout/Header/Header";
import ModalHuara from "./ModalHuara";
import ModalFactura from "./ModalFactura";
import { contratoPDF } from "./contratoPDF";
import {
  moneda,
  fechaCorta,
  reciboPagoPDF,
  estadoCuentaPDF,
  estadoCuentaExcel,
  agrupaFuturas,
  futurasEntregasPDF,
  futurasEntregasExcel,
} from "./pagosCxpPDF";
import { notaCxPPorId } from "../NotasCxP/notaCxPPDF";
import { entradaSinTallasPorNota } from "../NotasCxP/entradaSinTallasPDF";

// (2026-10) Pagos y Saldos de CxP: en una sola pantalla lo que se le debe a
// cada taller / proveedor, pagar (una o varias notas o compras), dar anticipos
// ligados a un contrato (salida a proceso), aplicarlos, editar la huara de las
// entradas, reimprimir recibos y sacar el estado de cuenta.

const URL = process.env.REACT_APP_URL_CXP_PAGOS;
const auth = () => ({ headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } });
const hoy = () => moment().format("YYYY-MM-DD");
const r2 = (n) => Math.round(Number(n || 0) * 100) / 100;
const errorDe = (err) => err.response?.data?.message || "Algo salió mal, intenta de nuevo";

const tarjeta = (titulo, valor, pie, color, onClick, activa) => (
  <div
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
    style={{
      flex: "1 1 180px",
      background: activa ? "#e8f0fb" : "#fff",
      border: "1px solid #e3e7ef",
      borderLeft: `5px solid ${color}`,
      borderRadius: 10,
      padding: "10px 16px",
      cursor: onClick ? "pointer" : undefined,
    }}
  >
    <div style={{ fontSize: 13, color: "#52514e" }}>{titulo}</div>
    <div style={{ fontSize: 24, fontWeight: 700, color: "#111827" }}>{valor}</div>
    {pie ? <div style={{ fontSize: 12, color: "#898781" }}>{pie}</div> : null}
  </div>
);

const VISTAS = [
  ["porPagar", "Por pagar"],
  ["contratos", "Contratos"],
  ["futuras", "Futuras entregas"],
  ["anticipos", "Anticipos"],
  ["pagos", "Pagos hechos"],
  ["estado", "Estado de cuenta"],
];

function PagosCxp() {
  const { user } = useContext(AuthContext);
  const puede = user.menu_cxpPagos || user.menu_adminCxP;

  const [resumen, setResumen] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [tipo, setTipo] = useState("Maquilero");
  const [buscar, setBuscar] = useState("");
  const [soloSaldo, setSoloSaldo] = useState(true);
  const [bancos, setBancos] = useState([]);

  const [selId, setSelId] = useState("");
  const [detalle, setDetalle] = useState(null);
  const [cargandoDet, setCargandoDet] = useState(false);
  const [vista, setVista] = useState("porPagar");
  const [marcados, setMarcados] = useState({});

  const [pago, setPago] = useState(null); // { filas, banco, fecha, observaciones }
  const [anticipo, setAnticipo] = useState(null); // { contrato, importe, banco, formaPago, fecha, observaciones }
  const [aplicar, setAplicar] = useState(null); // { anticipo, filas, fecha }
  const [huara, setHuara] = useState(null);
  const [factura, setFactura] = useState(null);
  const [guardando, setGuardando] = useState(false);
  // (2026-10) lo que falta que entreguen los talleres (contratos abiertos)
  const [futuras, setFuturas] = useState([]);
  const [verFuturas, setVerFuturas] = useState(false);

  const [edoInicio, setEdoInicio] = useState(moment().startOf("year").format("YYYY-MM-DD"));
  const [edoFin, setEdoFin] = useState("");
  const [edo, setEdo] = useState(null);

  // ---- carga
  function cargarResumen() {
    setCargando(true);
    return axios
      .get(`${URL}Resumen`, auth())
      .then((r) => setResumen(r.data || []))
      .catch((err) => Swal.fire("Error", errorDe(err), "error"))
      .finally(() => setCargando(false));
  }
  function cargarDetalle(id) {
    if (!id) return Promise.resolve();
    setCargandoDet(true);
    return axios
      .get(`${URL}Proveedor/${id}`, auth())
      .then((r) => setDetalle(r.data))
      .catch((err) => Swal.fire("Error", errorDe(err), "error"))
      .finally(() => setCargandoDet(false));
  }
  function cargarFuturas() {
    return axios
      .get(`${URL}FuturasEntregas`, auth())
      .then((r) => setFuturas(r.data || []))
      .catch((err) => console.log("futuras entregas:", err));
  }
  const refrescar = () => Promise.all([cargarResumen(), cargarDetalle(selId), cargarFuturas()]);

  useEffect(() => {
    cargarResumen();
    cargarFuturas();
    axios
      .get(process.env.REACT_APP_URL_BANCOS, auth())
      .then((r) =>
        setBancos(
          (r.data || []).filter(
            (b) => b.is_active !== "No" && !/devoluci|bonific|faltante/i.test(String(b.banco || ""))
          )
        )
      )
      .catch((e) => console.log(e));
  }, []);

  function abrir(id) {
    setVerFuturas(false);
    setSelId(id);
    setDetalle(null);
    setMarcados({});
    setVista("porPagar");
    setEdo(null);
    cargarDetalle(id);
  }

  // ---- lista
  const lista = useMemo(() => {
    const t = buscar.trim().toLowerCase();
    return resumen.filter(
      (r) =>
        (!tipo || r.proveedor.tipo === tipo) &&
        (!soloSaldo || r.porPagar > 0 || r.anticipos > 0) &&
        (!t || String(r.proveedor.nombre || "").toLowerCase().includes(t))
    );
  }, [resumen, tipo, buscar, soloSaldo]);

  const kpis = useMemo(() => {
    const base = resumen.filter((r) => !tipo || r.proveedor.tipo === tipo);
    return {
      porPagar: base.reduce((t, r) => t + r.porPagar, 0),
      vencido: base.reduce((t, r) => t + r.vencido, 0),
      anticipos: base.reduce((t, r) => t + r.anticipos, 0),
      conSaldo: base.filter((r) => r.porPagar > 0).length,
    };
  }, [resumen, tipo]);

  const totalFuturas = futuras.reduce((t, f) => t + f.aPagar, 0);
  const atrasadas = futuras.filter((f) => f.atrasada).length;
  const futurasTaller = futuras.filter((f) => String(f.proveedor?._id) === String(selId));

  const filaResumen = resumen.find((r) => String(r.proveedor._id) === String(selId));
  const esTaller = detalle?.proveedor?.tipo === "Maquilero";
  const documentos = detalle?.documentos || [];
  const seleccionados = documentos.filter((d) => marcados[d._id]);
  const totalSeleccionado = seleccionados.reduce((t, d) => t + d.saldo, 0);

  // ---- pagar
  function abrirPago(docs) {
    if (!docs.length) return Swal.fire("Atención", "Marca al menos una nota o compra", "warning");
    setPago({
      filas: docs.map((d) => ({ ...d, importe: d.saldo })),
      banco: bancos.length === 1 ? bancos[0]._id : "",
      fecha: hoy(),
      observaciones: "",
    });
  }
  async function guardarPago() {
    const filas = pago.filas.filter((f) => Number(f.importe) > 0);
    if (!pago.banco) return Swal.fire("Atención", "Elige el banco de donde sale el pago", "warning");
    if (!filas.length) return Swal.fire("Atención", "Captura cuánto se paga", "warning");
    const mal = filas.find((f) => r2(f.importe) > r2(f.saldo));
    if (mal) return Swal.fire("Atención", `En ${mal.tipo} ${mal.folio} el pago es mayor al saldo`, "warning");
    setGuardando(true);
    try {
      const { data } = await axios.post(
        `${URL}Pagar`,
        {
          proveedor: detalle.proveedor._id,
          banco: pago.banco,
          fecha: pago.fecha,
          observaciones: pago.observaciones,
          aplicaciones: filas.map((f) => ({ tipo: f.tipo, id: f._id, importe: r2(f.importe) })),
        },
        auth()
      );
      const banco = (bancos.find((b) => b._id === pago.banco) || {}).banco;
      const recibo = {
        proveedor: detalle.proveedor,
        fecha: pago.fecha,
        banco,
        observaciones: pago.observaciones,
        filas: filas.map((f) => ({
          documento: `${f.tipo} ${f.folio}`,
          detalle: f.tipo === "Nota" ? [f.contrato && `Contrato ${f.contrato.folio}`, f.articulo].filter(Boolean).join(" · ") : f.factura !== "NA" ? `Factura ${f.factura}` : "",
          importe: r2(f.importe),
        })),
      };
      setPago(null);
      setMarcados({});
      await refrescar();
      const r = await Swal.fire({
        title: "Pago registrado",
        text: `Se pagaron ${moneda(data.total)} en ${data.documentos} documento(s).`,
        icon: "success",
        showCancelButton: true,
        confirmButtonText: "Descargar recibo",
        cancelButtonText: "Cerrar",
      });
      if (r.isConfirmed) reciboPagoPDF(recibo);
    } catch (err) {
      Swal.fire("Error", errorDe(err), "error");
    } finally {
      setGuardando(false);
    }
  }

  // ---- anticipo
  function abrirAnticipo(contrato) {
    setAnticipo({
      contrato: contrato ? contrato._id : "",
      importe: "",
      banco: bancos.length === 1 ? bancos[0]._id : "",
      formaPago: "Transferencia",
      fecha: hoy(),
      observaciones: "",
    });
  }
  async function guardarAnticipo() {
    if (!anticipo.contrato) return Swal.fire("Atención", "Elige el contrato (salida) del anticipo", "warning");
    if (!(Number(anticipo.importe) > 0)) return Swal.fire("Atención", "Captura el importe del anticipo", "warning");
    if (!anticipo.banco) return Swal.fire("Atención", "Elige el banco de donde sale el anticipo", "warning");
    setGuardando(true);
    try {
      const { data } = await axios.post(
        `${URL}Anticipo`,
        {
          proveedor: detalle.proveedor._id,
          banco: anticipo.banco,
          fecha: anticipo.fecha,
          importe: r2(anticipo.importe),
          formaPago: anticipo.formaPago,
          procesosSalida: anticipo.contrato,
          observaciones: anticipo.observaciones,
        },
        auth()
      );
      const contrato = detalle.contratos.find((c) => c._id === anticipo.contrato) || {};
      const recibo = {
        tipo: "Anticipo",
        proveedor: detalle.proveedor,
        fecha: anticipo.fecha,
        banco: (bancos.find((b) => b._id === anticipo.banco) || {}).banco,
        observaciones: anticipo.observaciones,
        filas: [
          {
            documento: `Anticipo ${data.idAnticipoProveedores}`,
            detalle: [`Contrato ${contrato.folio}`, contrato.articulo, anticipo.formaPago].filter(Boolean).join(" · "),
            importe: r2(anticipo.importe),
          },
        ],
      };
      setAnticipo(null);
      await refrescar();
      const r = await Swal.fire({
        title: "Anticipo registrado",
        text: `Anticipo de ${moneda(anticipo.importe)} al contrato ${contrato.folio}.`,
        icon: "success",
        showCancelButton: true,
        confirmButtonText: "Descargar recibo",
        cancelButtonText: "Cerrar",
      });
      if (r.isConfirmed) reciboPagoPDF(recibo);
    } catch (err) {
      Swal.fire("Error", errorDe(err), "error");
    } finally {
      setGuardando(false);
    }
  }

  // ---- aplicar anticipo
  function abrirAplicar(ant) {
    const notas = documentos
      .filter((d) => d.tipo === "Nota")
      .map((d) => ({
        ...d,
        mismoContrato: !!(ant.contrato && d.contrato && d.contrato._id === ant.contrato._id),
        importe: 0,
      }))
      .sort((a, b) => Number(b.mismoContrato) - Number(a.mismoContrato) || String(a.fecha).localeCompare(String(b.fecha)));
    if (!notas.length) return Swal.fire("Sin notas", "Este taller no tiene notas pendientes para aplicar el anticipo", "info");
    setAplicar({ anticipo: ant, filas: notas, fecha: hoy() });
  }
  function repartir() {
    let resta = aplicar.anticipo.saldo;
    const filas = aplicar.filas.map((f) => {
      const usar = aplicar.anticipo.contrato ? f.mismoContrato : true;
      const importe = usar ? Math.min(f.saldo, Math.max(0, r2(resta))) : 0;
      resta = r2(resta - importe);
      return { ...f, importe };
    });
    setAplicar({ ...aplicar, filas });
  }
  async function guardarAplicar() {
    const filas = aplicar.filas.filter((f) => Number(f.importe) > 0);
    const total = r2(filas.reduce((t, f) => t + Number(f.importe), 0));
    if (!filas.length) return Swal.fire("Atención", "Captura cuánto se aplica a cada nota", "warning");
    if (total > aplicar.anticipo.saldo + 0.01) return Swal.fire("Atención", "Se está aplicando más de lo que queda del anticipo", "warning");
    const mal = filas.find((f) => r2(f.importe) > r2(f.saldo));
    if (mal) return Swal.fire("Atención", `En la nota ${mal.folio} el importe es mayor al saldo`, "warning");
    setGuardando(true);
    try {
      await axios.post(
        `${URL}AplicarAnticipo`,
        {
          anticipo: aplicar.anticipo._id,
          fecha: aplicar.fecha,
          aplicaciones: filas.map((f) => ({ nota: f._id, importe: r2(f.importe) })),
        },
        auth()
      );
      setAplicar(null);
      await refrescar();
      Swal.fire("Listo", `Se aplicaron ${moneda(total)} del anticipo ${aplicar.anticipo.folio}`, "success");
    } catch (err) {
      Swal.fire("Error", errorDe(err), "error");
    } finally {
      setGuardando(false);
    }
  }

  // ---- estado de cuenta
  function consultarEdo() {
    const q = new URLSearchParams();
    if (edoInicio) q.set("inicio", edoInicio);
    if (edoFin) q.set("fin", edoFin);
    axios
      .get(`${URL}EstadoCuenta/${selId}?${q.toString()}`, auth())
      .then((r) => setEdo(r.data))
      .catch((err) => Swal.fire("Error", errorDe(err), "error"));
  }
  useEffect(() => {
    if (vista === "estado" && selId && !edo) consultarEdo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vista, selId]);

  if (!puede) {
    return (
      <>
        <Header />
      </>
    );
  }

  const opcionBanco = (b) => (
    <option key={b._id} value={b._id}>
      {b.banco}
      {b.cuenta && b.cuenta !== b.banco ? ` · ${b.cuenta}` : ""}
    </option>
  );

  // (2026-10) futuras entregas agrupadas por semana de compromiso
  const tablaFuturas = (filas, conTaller) =>
    filas.length === 0 ? (
      <p className="text-muted">No hay piezas pendientes de entregar en contratos abiertos.</p>
    ) : (
      <Table size="sm" borderless className="table-responsive-xl">
        <thead>
          <tr>
            <th className="tituloTabla">Contrato</th>
            {conTaller ? <th className="tituloTabla">Taller</th> : null}
            <th className="tituloTabla">Artículo</th>
            <th className="tituloTabla">Compromiso</th>
            <th className="tituloTabla">Por entregar</th>
            <th className="tituloTabla">Importe</th>
            <th className="tituloTabla">Anticipo</th>
            <th className="tituloTabla">A pagar</th>
          </tr>
        </thead>
        <tbody>
          {agrupaFuturas(filas).map((g) => (
            <React.Fragment key={g.clave}>
              <tr style={{ background: g.clave === "0" ? "#fde2e2" : "#eef3fb" }}>
                <td colSpan={conTaller ? 5 : 4} style={{ fontWeight: 700, color: g.clave === "0" ? "#b91c1c" : "#1e3a8a" }}>
                  {g.etiqueta} · {g.filas.length} contrato(s) · {g.pendientes} pzs
                </td>
                <td align="right" style={{ fontWeight: 700 }}>{moneda(g.importe)}</td>
                <td></td>
                <td align="right" style={{ fontWeight: 700 }}>{moneda(g.aPagar)}</td>
              </tr>
              {g.filas.map((f) => (
                <tr key={f._id} style={{ borderBottom: "1px solid #eef0f4" }}>
                  <td>
                    <b>{f.folio}</b>
                    <br />
                    <small className="text-muted">
                      {f.proceso}
                      {f.corte ? ` · Corte ${f.corte}` : ""}
                    </small>
                  </td>
                  {conTaller ? (
                    <td>
                      <span role="button" style={{ cursor: "pointer", color: "#1e3a8a" }} onClick={() => abrir(f.proveedor?._id)}>
                        {f.proveedor?.nombre}
                      </span>
                    </td>
                  ) : null}
                  <td style={{ fontSize: 12 }}>{f.articulo}</td>
                  <td style={{ color: f.atrasada ? "#d03b3b" : undefined, whiteSpace: "nowrap" }}>
                    {fechaCorta(f.fechaCompromiso)}
                    {f.atrasada ? " ⚠" : ""}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {f.pendientes} de {f.piezas}
                    <br />
                    <small className="text-muted">× {moneda(f.precio)}</small>
                  </td>
                  <td align="right">{moneda(f.importe)}</td>
                  <td align="right">{f.anticipoDisponible > 0 ? moneda(f.anticipoDisponible) : "-"}</td>
                  <td align="right">
                    <b>{moneda(f.aPagar)}</b>
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </Table>
    );

  return (
    <>
      <Header />
      <br />
      <div className="container-fluid">
        <div className="card col-12" style={{ paddingBottom: 16 }}>
          <Row>
            <Col md={8}>
              <Button size="sm" href="/MenuCXP" className="btn btn-danger" id="botonListado">
                Regresar
              </Button>
            </Col>
          </Row>
          <h3 align="center">Pagos y Saldos</h3>

          {/* ---- resumen general */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
            {tarjeta("Por pagar", moneda(kpis.porPagar), `${kpis.conSaldo} con saldo`, "#003A5C")}
            {tarjeta("Vencido", moneda(kpis.vencido), "ya pasó su plazo", "#d03b3b")}
            {tarjeta("Anticipos sin aplicar", moneda(kpis.anticipos), "dinero ya entregado", "#8a6d1f")}
            {tarjeta(
              "Futuras entregas",
              moneda(totalFuturas),
              `${futuras.length} contratos por entregar${atrasadas ? ` · ${atrasadas} atrasados` : ""} · ver`,
              "#2E75B6",
              () => setVerFuturas((v) => !v),
              verFuturas
            )}
          </div>

          <Row>
            {/* ---- lista de talleres / proveedores */}
            <Col lg={4} style={{ marginBottom: 16 }}>
              <ButtonGroup style={{ marginBottom: 8, width: "100%" }}>
                {[
                  ["Maquilero", "Talleres"],
                  ["Proveedor", "Proveedores"],
                  ["", "Todos"],
                ].map(([v, l]) => (
                  <Button key={l} size="sm" color={tipo === v ? "info" : "secondary"} onClick={() => setTipo(v)}>
                    {l}
                  </Button>
                ))}
              </ButtonGroup>
              <Input
                bsSize="sm"
                placeholder="Buscar por nombre..."
                value={buscar}
                onChange={(e) => setBuscar(e.target.value)}
                style={{ marginBottom: 6 }}
              />
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 13,
                  margin: "0 0 8px 2px",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <input
                  type="checkbox"
                  checked={soloSaldo}
                  onChange={(e) => setSoloSaldo(e.target.checked)}
                  style={{ width: 16, height: 16, margin: 0, cursor: "pointer" }}
                />
                Sólo con saldo o anticipos
              </label>
              <div style={{ maxHeight: "65vh", overflowY: "auto", border: "1px solid #e3e7ef", borderRadius: 8 }}>
                {cargando ? (
                  <div style={{ textAlign: "center", padding: 30 }}>
                    <Spinner color="primary" />
                  </div>
                ) : lista.length === 0 ? (
                  <p className="text-muted" style={{ padding: 12 }}>
                    Nadie con esos filtros.
                  </p>
                ) : (
                  lista.map((r) => {
                    const activo = String(r.proveedor._id) === String(selId);
                    return (
                      <div
                        key={r.proveedor._id}
                        role="button"
                        tabIndex={0}
                        onClick={() => abrir(r.proveedor._id)}
                        onKeyDown={(e) => e.key === "Enter" && abrir(r.proveedor._id)}
                        style={{
                          padding: "8px 12px",
                          borderBottom: "1px solid #eef0f4",
                          cursor: "pointer",
                          background: activo ? "#e8f0fb" : "#fff",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                          <b style={{ fontSize: 13 }}>{r.proveedor.nombre}</b>
                          <b style={{ fontSize: 13 }}>{moneda(r.porPagar)}</b>
                        </div>
                        <div style={{ fontSize: 12, color: "#6b7280", display: "flex", justifyContent: "space-between" }}>
                          <span>
                            {r.documentos} pendiente(s)
                            {r.vencido > 0 ? <span style={{ color: "#d03b3b" }}> · vencido {moneda(r.vencido)}</span> : null}
                          </span>
                          {r.anticipos > 0 ? <span style={{ color: "#8a6d1f" }}>anticipo {moneda(r.anticipos)}</span> : null}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </Col>

            {/* ---- detalle */}
            <Col lg={8}>
              {verFuturas ? (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 8 }}>
                    <div>
                      <h4 style={{ marginBottom: 0 }}>Futuras entregas</h4>
                      <small className="text-muted">
                        Piezas que faltan de los contratos abiertos × su precio, menos el anticipo que ya tienen
                      </small>
                    </div>
                    <div>
                      <ButtonGroup id="logoutBoton">
                        <Button size="sm" color="danger" onClick={() => futurasEntregasPDF(futuras)} disabled={!futuras.length}>
                          PDF <i className="far fa-file-pdf"></i>
                        </Button>
                        <Button size="sm" color="success" onClick={() => futurasEntregasExcel(futuras)} disabled={!futuras.length}>
                          Excel <i className="far fa-file-excel"></i>
                        </Button>
                      </ButtonGroup>{" "}
                      <Button size="sm" color="secondary" onClick={() => setVerFuturas(false)}>
                        Cerrar
                      </Button>
                    </div>
                  </div>
                  {tablaFuturas(futuras, true)}
                </>
              ) : !selId ? (
                <div className="text-muted" style={{ padding: 40, textAlign: "center", border: "1px dashed #cfd5e1", borderRadius: 10 }}>
                  Elige un taller o proveedor de la lista para ver lo que se le debe y pagarle.
                </div>
              ) : !detalle || cargandoDet ? (
                <div style={{ textAlign: "center", padding: 40 }}>
                  <Spinner color="primary" />
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                    <div>
                      <h4 style={{ marginBottom: 0 }}>{detalle.proveedor.nombre}</h4>
                      <small className="text-muted">
                        {esTaller ? "Taller" : "Proveedor"}
                        {detalle.proveedor.plazo ? ` · plazo ${detalle.proveedor.plazo} días` : ""}
                      </small>
                    </div>
                    <div>
                      <Button color="success" size="sm" onClick={() => abrirPago(seleccionados.length ? seleccionados : documentos)} disabled={!documentos.length}>
                        <i className="fas fa-hand-holding-usd"></i> Registrar pago
                      </Button>{" "}
                      {esTaller ? (
                        <Button color="info" size="sm" onClick={() => abrirAnticipo(null)}>
                          <i className="fas fa-coins"></i> Dar anticipo
                        </Button>
                      ) : null}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "10px 0" }}>
                    {tarjeta("Debe", moneda(filaResumen?.porPagar), `${documentos.length} pendiente(s)`, "#003A5C")}
                    {tarjeta("Vencido", moneda(filaResumen?.vencido), "", "#d03b3b")}
                    {esTaller ? tarjeta("Anticipo disponible", moneda(filaResumen?.anticipos), "", "#8a6d1f") : null}
                  </div>

                  <ButtonGroup style={{ marginBottom: 10, flexWrap: "wrap" }}>
                    {VISTAS.filter(([v]) => esTaller || !["contratos", "futuras", "anticipos"].includes(v)).map(([v, l]) => (
                      <Button key={v} size="sm" color={vista === v ? "info" : "secondary"} onClick={() => setVista(v)}>
                        {l}
                        {v === "porPagar" ? ` (${documentos.length})` : ""}
                        {v === "contratos" ? ` (${detalle.contratos.length})` : ""}
                        {v === "futuras" ? ` (${futurasTaller.length})` : ""}
                        {v === "anticipos" ? ` (${detalle.anticipos.filter((a) => a.saldo > 0).length})` : ""}
                      </Button>
                    ))}
                  </ButtonGroup>

                  {/* ---- por pagar */}
                  {vista === "porPagar" ? (
                    documentos.length === 0 ? (
                      <p className="text-muted">No se le debe nada. 🎉</p>
                    ) : (
                      <>
                        <Table size="sm" striped borderless className="table-responsive-xl">
                          <thead>
                            <tr>
                              <th className="tituloTabla">
                                <Input
                                  type="checkbox"
                                  style={{ position: "static", margin: 0 }}
                                  checked={seleccionados.length === documentos.length}
                                  onChange={(e) =>
                                    setMarcados(e.target.checked ? Object.fromEntries(documentos.map((d) => [d._id, true])) : {})
                                  }
                                />
                              </th>
                              <th className="tituloTabla">Documento</th>
                              <th className="tituloTabla">Fecha</th>
                              <th className="tituloTabla">Vence</th>
                              <th className="tituloTabla">Detalle</th>
                              <th className="tituloTabla">Total</th>
                              <th className="tituloTabla">Pagado</th>
                              <th className="tituloTabla">Saldo</th>
                              <th className="tituloTabla"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {documentos.map((d) => (
                              <tr key={d._id}>
                                <td>
                                  <Input
                                    type="checkbox"
                                    style={{ position: "static", margin: 0 }}
                                    checked={!!marcados[d._id]}
                                    onChange={(e) => {
                                      const v = e.target.checked;
                                      setMarcados((p) => ({ ...p, [d._id]: v }));
                                    }}
                                  />
                                </td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                  <b>{d.tipo} {d.folio}</b>
                                </td>
                                <td>{fechaCorta(d.fecha)}</td>
                                <td style={{ color: d.vencida ? "#d03b3b" : undefined, whiteSpace: "nowrap" }}>
                                  {fechaCorta(d.vence)}
                                  {d.vencida ? " ⚠" : ""}
                                </td>
                                <td style={{ fontSize: 12 }}>
                                  {d.tipo === "Nota" ? (
                                    <>
                                      {d.contrato ? `Contrato ${d.contrato.folio}` : ""}
                                      {d.corte ? ` · Corte ${d.corte}` : ""}
                                      {d.articulo ? ` · ${d.articulo}` : ""}
                                      <br />
                                      {d.piezas} pzs × {moneda(d.precio)}
                                      {d.importeHuara > 0 ? ` + huara ${moneda(d.precioHuara)}` : ""}
                                      {d.iva > 0 ? (
                                        <>
                                          <br />
                                          <span style={{ color: "#1e3a8a" }}>
                                            {d.factura && d.factura !== "NA" ? `Factura ${d.factura} · ` : ""}
                                            IVA {moneda(d.iva)}
                                            {d.facturaParcial === "Si" ? ` (sobre ${moneda(d.importeFacturado)})` : ""}
                                          </span>
                                        </>
                                      ) : d.factura && d.factura !== "NA" ? (
                                        <>
                                          <br />
                                          <span style={{ color: "#1e3a8a" }}>Factura {d.factura}</span>
                                        </>
                                      ) : null}
                                    </>
                                  ) : d.tipo === "Compra PT" ? (
                                    <>
                                      {/* (2026-10) compra de producto terminado (ropa) */}
                                      {d.oc ? `OC ${d.oc} · ` : ""}
                                      {d.piezas} pzs de producto terminado
                                      {d.factura && d.factura !== "NA" ? (
                                        <>
                                          <br />
                                          <span style={{ color: "#1e3a8a" }}>Factura {d.factura}</span>
                                        </>
                                      ) : null}
                                    </>
                                  ) : d.factura && d.factura !== "NA" ? (
                                    `Factura ${d.factura}`
                                  ) : (
                                    ""
                                  )}
                                </td>
                                <td align="right">{moneda(d.total)}</td>
                                <td align="right">{d.abonado > 0 ? moneda(d.abonado) : "-"}</td>
                                <td align="right">
                                  <b>{moneda(d.saldo)}</b>
                                </td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                  <Button size="sm" color="success" title="Pagar esta" onClick={() => abrirPago([d])}>
                                    <i className="fas fa-dollar-sign"></i>
                                  </Button>
                                  {d.tipo === "Nota" ? (
                                    <>
                                      <Button size="sm" color="danger" title="PDF de la nota" onClick={() => notaCxPPorId(d._id)}>
                                        <i className="far fa-file-pdf"></i>
                                      </Button>
                                      <Button size="sm" color="primary" title="PDF de la entrada sin tallas" onClick={() => entradaSinTallasPorNota(d._id)}>
                                        <i className="fas fa-file-alt"></i>
                                      </Button>
                                      {d.entrada ? (
                                        <Button
                                          size="sm"
                                          color="warning"
                                          title="Huara"
                                          onClick={() =>
                                            setHuara({
                                              folio: d.folio,
                                              idEntrada: d.entrada._id,
                                              piezas: d.piezas,
                                              precio: d.precio,
                                              precioHuara: d.precioHuara,
                                              importeHuara: d.importeHuara,
                                              total: d.total,
                                              abonado: d.abonado,
                                              impuestos: d.impuestos,
                                              subtotal: d.subtotal,
                                              facturaParcial: d.facturaParcial,
                                              importeFacturado: d.importeFacturado,
                                            })
                                          }
                                        >
                                          {d.precioHuara > 0 ? `H ${moneda(d.precioHuara)}` : "+ Huara"}
                                        </Button>
                                      ) : null}
                                      <Button size="sm" color="dark" title="Factura e IVA del taller" onClick={() => setFactura(d)}>
                                        {d.impuestos > 0 ? `IVA ${d.impuestos}%` : "+ Factura"}
                                      </Button>
                                    </>
                                  ) : null}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12 }}>
                          <span>
                            {seleccionados.length} marcada(s): <b>{moneda(totalSeleccionado)}</b>
                          </span>
                          <Button color="success" onClick={() => abrirPago(seleccionados)} disabled={!seleccionados.length}>
                            Pagar marcadas
                          </Button>
                        </div>
                      </>
                    )
                  ) : null}

                  {/* ---- contratos */}
                  {vista === "contratos" ? (
                    detalle.contratos.length === 0 ? (
                      <p className="text-muted">Sin contratos abiertos.</p>
                    ) : (
                      <Table size="sm" striped borderless className="table-responsive-xl">
                        <thead>
                          <tr>
                            <th className="tituloTabla">Contrato</th>
                            <th className="tituloTabla">Fecha</th>
                            <th className="tituloTabla">Artículo</th>
                            <th className="tituloTabla">Piezas</th>
                            <th className="tituloTabla">Valor</th>
                            <th className="tituloTabla">Entregado</th>
                            <th className="tituloTabla">Por pagar</th>
                            <th className="tituloTabla">Anticipado</th>
                            <th className="tituloTabla"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {detalle.contratos.map((c) => (
                            <tr key={c._id}>
                              <td>
                                <b>{c.folio}</b>
                                <br />
                                <small className="text-muted">{c.proceso}{c.corte ? ` · Corte ${c.corte}` : ""}</small>
                              </td>
                              <td>{fechaCorta(c.fecha)}</td>
                              <td style={{ fontSize: 12 }}>{c.articulo}</td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {c.recibidas} / {c.piezas}
                                <br />
                                <small className="text-muted">recibidas</small>
                              </td>
                              <td align="right">{moneda(c.valor)}</td>
                              <td align="right">{moneda(c.facturado)}</td>
                              <td align="right">
                                <b>{moneda(c.porPagar)}</b>
                              </td>
                              <td align="right">
                                {c.anticipado > 0 ? (
                                  <>
                                    {moneda(c.anticipado)}
                                    <br />
                                    <small style={{ color: "#8a6d1f" }}>disponible {moneda(c.anticipoDisponible)}</small>
                                  </>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                <Button size="sm" color="danger" title="PDF del contrato con sus entregas" onClick={() => contratoPDF(c._id)}>
                                  <i className="far fa-file-pdf"></i>
                                </Button>
                                <Button size="sm" color="info" onClick={() => abrirAnticipo(c)}>
                                  Anticipo
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )
                  ) : null}

                  {/* ---- (2026-10) futuras entregas de este taller */}
                  {vista === "futuras" ? (
                    <>
                      {futurasTaller.length ? (
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, alignItems: "center", marginBottom: 6 }}>
                          <span>
                            Por pagar al entregar: <b>{moneda(futurasTaller.reduce((t, f) => t + f.aPagar, 0))}</b>
                          </span>
                          <Button
                            size="sm"
                            color="danger"
                            onClick={() => futurasEntregasPDF(futurasTaller, `FUTURAS ENTREGAS · ${detalle.proveedor.nombre}`)}
                          >
                            PDF <i className="far fa-file-pdf"></i>
                          </Button>
                        </div>
                      ) : null}
                      {tablaFuturas(futurasTaller, false)}
                    </>
                  ) : null}

                  {/* ---- anticipos */}
                  {vista === "anticipos" ? (
                    detalle.anticipos.length === 0 ? (
                      <p className="text-muted">Sin anticipos. Se dan desde "Dar anticipo" o desde un contrato.</p>
                    ) : (
                      <Table size="sm" striped borderless className="table-responsive-xl">
                        <thead>
                          <tr>
                            <th className="tituloTabla">Anticipo</th>
                            <th className="tituloTabla">Fecha</th>
                            <th className="tituloTabla">Contrato</th>
                            <th className="tituloTabla">Importe</th>
                            <th className="tituloTabla">Aplicado</th>
                            <th className="tituloTabla">Disponible</th>
                            <th className="tituloTabla">Banco</th>
                            <th className="tituloTabla"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {detalle.anticipos.map((a) => (
                            <tr key={a._id}>
                              <td>
                                <b>{a.folio}</b>
                              </td>
                              <td>{fechaCorta(a.fecha)}</td>
                              <td>{a.contrato ? a.contrato.folio : <span className="text-muted">-</span>}</td>
                              <td align="right">{moneda(a.importe)}</td>
                              <td align="right">{moneda(a.utilizado)}</td>
                              <td align="right">
                                <b style={{ color: a.saldo > 0 ? "#8a6d1f" : undefined }}>{moneda(a.saldo)}</b>
                              </td>
                              <td>{a.banco}</td>
                              <td>
                                {a.saldo > 0 ? (
                                  <Button size="sm" color="warning" onClick={() => abrirAplicar(a)}>
                                    Aplicar a notas
                                  </Button>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )
                  ) : null}

                  {/* ---- pagos hechos */}
                  {vista === "pagos" ? (
                    detalle.pagos.length === 0 ? (
                      <p className="text-muted">Todavía no hay pagos registrados.</p>
                    ) : (
                      <Table size="sm" striped borderless className="table-responsive-xl">
                        <thead>
                          <tr>
                            <th className="tituloTabla">Fecha</th>
                            <th className="tituloTabla">Tipo</th>
                            <th className="tituloTabla">Banco</th>
                            <th className="tituloTabla">Documentos</th>
                            <th className="tituloTabla">Total</th>
                            <th className="tituloTabla"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {detalle.pagos.map((p) => (
                            <tr key={p.codigoAgrupado}>
                              <td>{fechaCorta(p.fecha)}</td>
                              <td>{p.tipo === "Pago" ? "Pago" : `Aplicación anticipo ${p.anticipo}`}</td>
                              <td>{p.tipo === "Pago" ? p.banco : "-"}</td>
                              <td style={{ fontSize: 12 }}>{p.documentos.map((x) => `${x.documento}: ${moneda(x.importe)}`).join(" · ")}</td>
                              <td align="right">
                                <b>{moneda(p.total)}</b>
                              </td>
                              <td>
                                {p.tipo === "Pago" ? (
                                  <Button
                                    size="sm"
                                    color="danger"
                                    title="Recibo"
                                    onClick={() =>
                                      reciboPagoPDF({
                                        proveedor: detalle.proveedor,
                                        fecha: p.fecha,
                                        banco: p.banco,
                                        observaciones: p.observaciones,
                                        filas: p.documentos,
                                      })
                                    }
                                  >
                                    <i className="far fa-file-pdf"></i>
                                  </Button>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )
                  ) : null}

                  {/* ---- estado de cuenta */}
                  {vista === "estado" ? (
                    <>
                      <Row className="align-items-end" style={{ marginBottom: 8 }}>
                        <Col md={3}>
                          <Label>Desde</Label>
                          <Input type="date" bsSize="sm" value={edoInicio} onChange={(e) => setEdoInicio(e.target.value)} />
                        </Col>
                        <Col md={3}>
                          <Label>Hasta</Label>
                          <Input type="date" bsSize="sm" value={edoFin} onChange={(e) => setEdoFin(e.target.value)} />
                        </Col>
                        <Col md={6}>
                          <Button size="sm" color="success" onClick={consultarEdo}>
                            Consultar
                          </Button>{" "}
                          <ButtonGroup>
                            <Button size="sm" color="danger" onClick={() => edo && estadoCuentaPDF(edo)} disabled={!edo}>
                              PDF <i className="far fa-file-pdf"></i>
                            </Button>
                            <Button size="sm" color="primary" onClick={() => edo && estadoCuentaExcel(edo)} disabled={!edo}>
                              Excel <i className="far fa-file-excel"></i>
                            </Button>
                          </ButtonGroup>
                        </Col>
                      </Row>
                      {!edo ? (
                        <div style={{ textAlign: "center", padding: 20 }}>
                          <Spinner color="primary" />
                        </div>
                      ) : (
                        <Table size="sm" striped borderless className="table-responsive-xl">
                          <thead>
                            <tr>
                              <th className="tituloTabla">Fecha</th>
                              <th className="tituloTabla">Movimiento</th>
                              <th className="tituloTabla">Concepto</th>
                              <th className="tituloTabla">Cargo</th>
                              <th className="tituloTabla">Abono</th>
                              <th className="tituloTabla">Saldo</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td colSpan={5}>
                                <b>Saldo inicial</b>
                              </td>
                              <td align="right">
                                <b>{moneda(edo.saldoInicial)}</b>
                              </td>
                            </tr>
                            {edo.movimientos.map((m, i) => (
                              <tr key={i} style={m.tipo === "Aplicación" ? { color: "#6b7280" } : undefined}>
                                <td>{fechaCorta(m.fecha)}</td>
                                <td>{m.tipo}</td>
                                <td style={{ fontSize: 12 }}>{m.concepto}</td>
                                <td align="right">{m.cargo ? moneda(m.cargo) : ""}</td>
                                <td align="right">{m.abono ? moneda(m.abono) : ""}</td>
                                <td align="right">{moneda(m.saldo)}</td>
                              </tr>
                            ))}
                            <tr>
                              <td colSpan={3}>
                                <b>Totales del periodo</b>
                              </td>
                              <td align="right">
                                <b>{moneda(edo.cargos)}</b>
                              </td>
                              <td align="right">
                                <b>{moneda(edo.abonos)}</b>
                              </td>
                              <td align="right">
                                <b>{moneda(edo.saldoFinal)}</b>
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                      )}
                    </>
                  ) : null}
                </>
              )}
            </Col>
          </Row>
        </div>
      </div>

      {/* ---- modal: pago */}
      <Modal size="lg" isOpen={!!pago} toggle={() => setPago(null)}>
        <ModalHeader toggle={() => setPago(null)}>Registrar pago · {detalle?.proveedor?.nombre}</ModalHeader>
        {pago ? (
          <>
            <ModalBody>
              <Row>
                <Col md={4}>
                  <Label>Fecha</Label>
                  <Input type="date" value={pago.fecha} onChange={(e) => setPago({ ...pago, fecha: e.target.value })} />
                </Col>
                <Col md={4}>
                  <Label>Sale del banco *</Label>
                  <Input type="select" value={pago.banco} onChange={(e) => setPago({ ...pago, banco: e.target.value })}>
                    <option value="">Selecciona...</option>
                    {bancos.map(opcionBanco)}
                  </Input>
                </Col>
                <Col md={4}>
                  <Label>Observaciones</Label>
                  <Input value={pago.observaciones} onChange={(e) => setPago({ ...pago, observaciones: e.target.value })} />
                </Col>
              </Row>
              <br />
              <Table size="sm" borderless>
                <thead>
                  <tr>
                    <th>Documento</th>
                    <th>Saldo</th>
                    <th style={{ width: 160 }}>Se paga</th>
                  </tr>
                </thead>
                <tbody>
                  {pago.filas.map((f, i) => (
                    <tr key={f._id}>
                      <td>
                        <b>{f.tipo} {f.folio}</b>{" "}
                        <small className="text-muted">{f.tipo === "Nota" && f.contrato ? `Contrato ${f.contrato.folio}` : ""}</small>
                      </td>
                      <td>{moneda(f.saldo)}</td>
                      <td>
                        <Input
                          type="number"
                          min="0"
                          step="any"
                          bsSize="sm"
                          value={f.importe}
                          invalid={r2(f.importe) > r2(f.saldo)}
                          onChange={(e) => {
                            const v = e.target.value;
                            setPago((p) => ({ ...p, filas: p.filas.map((x, j) => (j === i ? { ...x, importe: v } : x)) }));
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <h4 style={{ textAlign: "right" }}>
                Total a pagar: {moneda(pago.filas.reduce((t, f) => t + Number(f.importe || 0), 0))}
              </h4>
            </ModalBody>
            <ModalFooter>
              <Button color="success" onClick={guardarPago} disabled={guardando}>
                {guardando ? "Guardando..." : "Guardar pago"}
              </Button>
            </ModalFooter>
          </>
        ) : null}
      </Modal>

      {/* ---- modal: anticipo */}
      <Modal isOpen={!!anticipo} toggle={() => setAnticipo(null)}>
        <ModalHeader toggle={() => setAnticipo(null)}>Dar anticipo · {detalle?.proveedor?.nombre}</ModalHeader>
        {anticipo ? (
          <>
            <ModalBody>
              <Label>Contrato (salida al taller) *</Label>
              <Input type="select" value={anticipo.contrato} onChange={(e) => setAnticipo({ ...anticipo, contrato: e.target.value })}>
                <option value="">Selecciona...</option>
                {(detalle?.contratos || []).map((c) => (
                  <option key={c._id} value={c._id}>
                    {`Contrato ${c.folio} · ${fechaCorta(c.fecha)} · ${c.articulo || c.proceso} · ${c.piezas} pzs · ${moneda(c.valor)}`}
                  </option>
                ))}
              </Input>
              <br />
              <Row>
                <Col md={6}>
                  <Label>Importe *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="any"
                    value={anticipo.importe}
                    onChange={(e) => setAnticipo({ ...anticipo, importe: e.target.value })}
                  />
                </Col>
                <Col md={6}>
                  <Label>Fecha</Label>
                  <Input type="date" value={anticipo.fecha} onChange={(e) => setAnticipo({ ...anticipo, fecha: e.target.value })} />
                </Col>
              </Row>
              <br />
              <Row>
                <Col md={6}>
                  <Label>Sale del banco *</Label>
                  <Input type="select" value={anticipo.banco} onChange={(e) => setAnticipo({ ...anticipo, banco: e.target.value })}>
                    <option value="">Selecciona...</option>
                    {bancos.map(opcionBanco)}
                  </Input>
                </Col>
                <Col md={6}>
                  <Label>Forma de pago</Label>
                  <Input type="select" value={anticipo.formaPago} onChange={(e) => setAnticipo({ ...anticipo, formaPago: e.target.value })}>
                    <option>Transferencia</option>
                    <option>Efectivo</option>
                    <option>Cheque</option>
                  </Input>
                </Col>
              </Row>
              <br />
              <Label>Observaciones</Label>
              <Input value={anticipo.observaciones} onChange={(e) => setAnticipo({ ...anticipo, observaciones: e.target.value })} />
            </ModalBody>
            <ModalFooter>
              <Button color="success" onClick={guardarAnticipo} disabled={guardando}>
                {guardando ? "Guardando..." : "Guardar anticipo"}
              </Button>
            </ModalFooter>
          </>
        ) : null}
      </Modal>

      {/* ---- modal: aplicar anticipo */}
      <Modal size="lg" isOpen={!!aplicar} toggle={() => setAplicar(null)}>
        <ModalHeader toggle={() => setAplicar(null)}>
          Aplicar anticipo {aplicar?.anticipo?.folio}
          {aplicar?.anticipo?.contrato ? ` · contrato ${aplicar.anticipo.contrato.folio}` : ""}
        </ModalHeader>
        {aplicar ? (
          <>
            <ModalBody>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span>
                  Disponible: <b>{moneda(aplicar.anticipo.saldo)}</b>
                </span>
                <Button size="sm" color="info" onClick={repartir}>
                  Repartir automático{aplicar.anticipo.contrato ? " (notas del contrato)" : ""}
                </Button>
              </div>
              <Table size="sm" borderless>
                <thead>
                  <tr>
                    <th>Nota</th>
                    <th>Fecha</th>
                    <th>Saldo</th>
                    <th style={{ width: 160 }}>Se aplica</th>
                  </tr>
                </thead>
                <tbody>
                  {aplicar.filas.map((f, i) => (
                    <tr key={f._id} style={f.mismoContrato ? { background: "#fdf6e3" } : undefined}>
                      <td>
                        <b>Nota {f.folio}</b>{" "}
                        <small className="text-muted">
                          {f.contrato ? `Contrato ${f.contrato.folio}` : ""}
                          {f.mismoContrato ? " · mismo contrato" : ""}
                        </small>
                      </td>
                      <td>{fechaCorta(f.fecha)}</td>
                      <td>{moneda(f.saldo)}</td>
                      <td>
                        <Input
                          type="number"
                          min="0"
                          step="any"
                          bsSize="sm"
                          value={f.importe}
                          invalid={r2(f.importe) > r2(f.saldo)}
                          onChange={(e) => {
                            const v = e.target.value;
                            setAplicar((p) => ({ ...p, filas: p.filas.map((x, j) => (j === i ? { ...x, importe: v } : x)) }));
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {(() => {
                const total = aplicar.filas.reduce((t, f) => t + Number(f.importe || 0), 0);
                return (
                  <h5 style={{ textAlign: "right", color: total > aplicar.anticipo.saldo + 0.01 ? "#d03b3b" : undefined }}>
                    Se aplican {moneda(total)} · quedan {moneda(aplicar.anticipo.saldo - total)}
                  </h5>
                );
              })()}
            </ModalBody>
            <ModalFooter>
              <Button color="success" onClick={guardarAplicar} disabled={guardando}>
                {guardando ? "Guardando..." : "Aplicar"}
              </Button>
            </ModalFooter>
          </>
        ) : null}
      </Modal>

      <ModalHuara isOpen={!!huara} toggle={() => setHuara(null)} nota={huara} onSaved={refrescar} />
      <ModalFactura isOpen={!!factura} toggle={() => setFactura(null)} nota={factura} onSaved={refrescar} />
    </>
  );
}

export default PagosCxp;
